import { FindDocumentsQuery } from 'documents/dtos/document'
import { Id } from 'types/definitions'
import { computed, makeObservable } from 'mobx'
import DocumentStore from 'documents/DocumentStore'
import Document from 'documents/Document'
import { GetCurrentMember } from 'iam/SessionStore'
import DocumentVersionStore from '../DocumentVersionStore'
import InfiniteScrollProvider from 'utils/InfiniteScrollProvider'
import { AlertFactory } from 'alerts/Alert'
import { GetCurrentUserWorkspaceMemberships } from 'iam/WorkspaceMembershipStore'
import DocumentTagStore from 'documents/DocumentTagStore'
import Workspace from 'iam/models/Workspace'
import DocumentTag from 'documents/DocumentTag'
import { DocumentActionsAdapter } from '../DocumentActionsAdapter'

export interface UserDocumentsViewModelPort {
  /**
   * Getter for the current member (selected workspace).
   */
  getCurrentMember: GetCurrentMember

  /**
   * Getter for current user workspaces.
   */
  getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMemberships

  /**
   * Returns the documents that pertain to whichever
   * subject we are working with
   */
  readonly documents: Document[]

  /**
   * Returns a scroll provider for fetching
   * user documents in the specified workspace
   * @param workspaceId
   */
  getWorkspaceDocsScrollProvider: (
    workspaceId: string
  ) => InfiniteScrollProvider<Document, Record<string, unknown>>

  /**
   * Returns a scroll provider for fetching
   * user documents accross all workspaces
   */
  getUserDocsScrollProvider: () => InfiniteScrollProvider<
    Document,
    Record<string, unknown>
  >

  /**
   * fetcher for document tags
   */
  fetchTags: DocumentTagStore['fetchTags']

  /**
   * Getter for tags based on a workspace.
   * If no workspace is specified (because we're showing
   * docs across workspaces for example) it we should get
   * all the tags available
   */
  tagsIn: (workspace?: Workspace) => DocumentTag[]

  /**
   * Alert factory
   */
  newAlert: AlertFactory

  /**
   * Adapter for common document actions (download/archive/uploads)
   */
  documentActionsAdapter: DocumentActionsAdapter
}

export class UserDocumentsViewModelAdapter
  implements UserDocumentsViewModelPort
{
  constructor(
    public getCurrentMember: GetCurrentMember,
    public getCurrentUserWorkspaceMemberships: GetCurrentUserWorkspaceMemberships,
    private documentStore: DocumentStore,
    private documentVersionStore: DocumentVersionStore,
    private documentTagStore: DocumentTagStore,
    public fetchTags: DocumentTagStore['fetchTags'],
    public newAlert: AlertFactory,
    public documentActionsAdapter: DocumentActionsAdapter
  ) {
    makeObservable(this)
  }

  @computed
  get documents() {
    const userDocs = this.documentStore.forUser(this.getCurrentMember().user)
    return userDocs
  }

  tagsIn(workspace?: Workspace) {
    if (workspace) {
      return this.documentTagStore.tags.filter(
        (t) => t.workspaceId === workspace.id
      )
    }

    return this.documentTagStore.tags.items
  }

  getWorkspaceDocsScrollProvider(workspaceId: string) {
    return this.documentStore.getScrollProvider(
      this.getKeyForWorkspaceDocsScrollProvider(workspaceId),
      (params) => {
        return this.documentStore.findDocuments({
          ...this.getFetchParamsFor(workspaceId),
          ...params,
        })
      }
    )
  }

  getUserDocsScrollProvider() {
    return this.documentStore.getScrollProvider(
      this.getKeyForUserDocsScrollProvider(),
      (params) => {
        return this.documentStore.findDocuments({
          ...this.getFetchParamsFor(),
          ...params,
        })
      }
    )
  }

  private getKeyForWorkspaceDocsScrollProvider(workspaceId: string) {
    const userId = this.getCurrentMember().user.id
    return `UserDocuments/${workspaceId}/${userId}`
  }

  private getKeyForUserDocsScrollProvider() {
    const userId = this.getCurrentMember().user.id
    return `UserDocuments/${userId}`
  }

  private getFetchParamsFor(workspaceId?: Id): FindDocumentsQuery {
    return {
      context: 'User',
      workspace_id: workspaceId,
      accesses: true,
    }
  }
}
